import React from "react";
import { Box, Stack, Typography, Paper, Avatar, Divider, Chip, alpha } from "@mui/material";
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';

// Building blocks shared by every patient form (the full "Add Patient"
// page, the "Quick Add" page, and the Edit page) so all three stay
// visually identical instead of drifting apart over time.

export const exerciseOptions = [
    "Light exercise (yoga, stretching)",
    "Walk (light, brisk, jog)",
    "Weight/resistance training",
    "None",
];

export const physiologicalOptions = [
    "Obesity",
    "Hypothyroid",
    "Diabetes mellitus",
    "Hypertension",
    "Dyslipidemia",
    "Acidity",
    "Constipation",
    "Any deficiency",
    "Arthritis",
    "Other",
];

// A consistent "card" wrapper for each group of fields, with an icon +
// title header so a long form reads as clear steps instead of one wall
// of inputs.
export function Section({ icon, title, subtitle, action, children }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 3,
                borderColor: 'divider',
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <Avatar
                    sx={(theme) => ({
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: 'primary.main',
                        width: 40,
                        height: 40,
                    })}
                >
                    {icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {action}
            </Stack>
            <Divider sx={{ my: 2.5 }} />
            <Stack spacing={2.5}>{children}</Stack>
        </Paper>
    );
}

// Lays fields out side-by-side on desktop, stacked on mobile — using plain
// CSS grid (not MUI's <Grid>, which relies on negative margins that can
// throw a row's left edge out of alignment with sibling elements outside
// the grid). `columns` is how many equal-width fields sit in one row at
// the sm breakpoint; every field still gets its own full row on mobile.
export function FieldRow({ columns = 2, children }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, 1fr)` },
                gap: 2,
            }}
        >
            {children}
        </Box>
    );
}

// Renders a single-select field (a RadioGroup, visually) as a row of
// individually-spaced pill chips — quicker to scan and tap than stacked
// radios. Plain Chips (not ToggleButtonGroup) so each pill stands on its
// own instead of sitting inside one grouped, shadowed container.
export function ChoiceField({ label, error, options, value, onChange, disabled }) {
    return (
        <Box>
            <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mb: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    · choose one
                </Typography>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                {options.map((option) => {
                    const selected = value === option;
                    return (
                        <Chip
                            key={option}
                            label={option}
                            clickable={!disabled}
                            disabled={disabled}
                            icon={selected ? <RadioButtonCheckedRoundedIcon /> : <RadioButtonUncheckedRoundedIcon />}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => !disabled && onChange(option)}
                            sx={{ fontSize: '0.85rem', py: 2.25, px: 0.5 }}
                        />
                    );
                })}
            </Box>
            {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}

// Same idea as ChoiceField, but several chips can be selected at once
// (stacked checkboxes, visually). The "choose one" vs "select any" caption
// plus radio-dot vs checkbox icons are the two visual cues that tell them
// apart at a glance.
export function MultiChoiceField({ label, error, options, value, onChange, disabled }) {
    const toggle = (option) => {
        if (value.includes(option)) {
            onChange(value.filter((item) => item !== option));
        } else {
            onChange([...value, option]);
        }
    };

    return (
        <Box>
            <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mb: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    · select any that apply
                </Typography>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                {options.map((option) => {
                    const selected = value.includes(option);
                    return (
                        <Chip
                            key={option}
                            label={option}
                            clickable={!disabled}
                            disabled={disabled}
                            icon={selected ? <CheckBoxRoundedIcon /> : <CheckBoxOutlineBlankRoundedIcon />}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => !disabled && toggle(option)}
                            sx={{ fontSize: '0.85rem', py: 2.25, px: 0.5 }}
                        />
                    );
                })}
            </Box>
            {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}
